import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Download, Star, LogIn, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const DownloadPaywall = ({ isOpen, onClose, mediaTitle, mediaType = 'song', uploadId }) => {
    const { isAuthenticated, hasActiveSubscription, user } = useAuth();
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);

    // Official Artists (admin-granted) get download access without a subscription,
    // matching the same exemption the backend already grants them.
    const canDownloadFree = hasActiveSubscription || !!user?.profile?.isOfficialArtist;

    if (!isOpen) return null;

    const handleDownload = async () => {
        if (!uploadId) {
            setDownloadError('Unable to identify this file. Please try again.');
            return;
        }
        setDownloading(true);
        setDownloadError(null);
        try {
            const response = await api.get(`/upload/${uploadId}/download`);
            if (response.data?.success) {
                const { downloadUrl, filename } = response.data;
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename || 'download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                onClose();
            }
        } catch (err) {
            const msg = err?.message || 'Download failed. Please try again.';
            setDownloadError(msg);
        } finally {
            setDownloading(false);
        }
    };

    const typeLabel = mediaType === 'song' ? 'track' : mediaType;

    const modalContent = (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="glass-card max-w-md w-full border border-blue-600/30 relative animate-fade-in">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Header */}
                <div className="p-6 border-b border-gray-700/50">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                            <Download className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Download</h2>
                            <p className="text-sm text-gray-400 truncate max-w-[220px]">"{mediaTitle}"</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {/* Not logged in */}
                    {!isAuthenticated && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <LogIn className="w-8 h-8 text-blue-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">Sign in to download</h3>
                            <p className="text-sm text-gray-400 mb-6">
                                Create a free account and become a supporter to download {typeLabel}s from The Deep Wave.
                            </p>
                            <div className="flex flex-col gap-3">
                                <Link
                                    to="/login"
                                    onClick={onClose}
                                    className="w-full btn-primary flex items-center gap-2 justify-center"
                                >
                                    <LogIn className="w-4 h-4" />
                                    Sign In
                                </Link>
                                <Link
                                    to="/register"
                                    onClick={onClose}
                                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors text-center"
                                >
                                    Create Account
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Logged in, no subscription (and not an Official Artist) */}
                    {isAuthenticated && !canDownloadFree && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-yellow-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">Membership required</h3>
                            <p className="text-sm text-gray-400 mb-6">
                                An active subscription gives you unlimited downloads of all Deep Wave {typeLabel}s, images, and videos.
                            </p>
                            <div className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 border border-blue-600/30 rounded-xl p-4 mb-4 text-left">
                                <ul className="text-sm text-gray-300 space-y-2">
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        Unlimited downloads — audio, video, images
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        High-quality original files
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        Support The Deep Wave directly
                                    </li>
                                </ul>
                            </div>
                            <Link
                                to="/subscribe"
                                onClick={onClose}
                                className="w-full btn-primary flex items-center gap-2 justify-center"
                            >
                                <Star className="w-4 h-4" />
                                View Membership Plans
                            </Link>
                        </div>
                    )}

                    {/* Has subscription or Official Artist status — download */}
                    {isAuthenticated && canDownloadFree && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-1">Ready to download</h3>
                            <p className="text-sm text-gray-400 mb-6">
                                Your membership gives you access to this {typeLabel}.
                            </p>
                            {downloadError && (
                                <div className="flex items-center gap-2 text-red-400 bg-red-400/10 rounded-lg p-3 mb-4 text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {downloadError}
                                </div>
                            )}
                            <button
                                onClick={handleDownload}
                                disabled={downloading}
                                className="w-full btn-primary flex items-center gap-2 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {downloading ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        Preparing download…
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Download {typeLabel}
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

export default DownloadPaywall;
